<template>
  <div v-if="report" class="screen finish-screen" @click="skipReveal">
    <h2>The race is run</h2>

    <p v-if="shown >= plan.result" class="finish-screen__result score-row--enter">
      <CamelChip :camel="report.winner" /> takes the race,
      <CamelChip :camel="report.loser" /> brings up the rear.
    </p>

    <!-- The face-down decks, turned over card by card in placement order
         (rules p4) - and paced that way too: each card is its own beat, so
         the 8/5/3/2 ladder lands one gasp at a time instead of all at once.
         A tap anywhere deals the rest of the sheet immediately. -->
    <template v-for="(pile, p) in report.piles" :key="pile.pile">
      <div v-if="shown >= plan.piles[p].header" class="bet-reveal score-row--enter">
        <h3>
          Overall {{ pile.pile }}:
          <CamelChip :camel="pile.target" />
        </h3>
        <p v-if="!pile.cards.length && shown >= plan.piles[p].cards" class="muted score-row--enter">Nobody bet.</p>
        <div v-else-if="pile.cards.length" class="bet-reveal__cards">
          <span
            v-for="(card, i) in visibleCards(p)"
            :key="i"
            class="bet-reveal__card score-row--enter"
            :class="[`bet-reveal__card--${card.camel}`, { 'is-right': card.camel === pile.target }]"
          >
            <img :src="`art/camel-${card.camel}.jpg`" :alt="card.camel" />
            <span class="bet-reveal__who">{{ card.name }}</span>
            <span class="bet-reveal__gain" :class="{ 'is-up': card.gain > 0, 'is-down': card.gain < 0 }">
              {{ card.gain >= 0 ? '+' : '' }}{{ card.gain }}
            </span>
          </span>
        </div>
      </div>
    </template>

    <!-- The standings come LAST - who won the game is what the whole
         reveal builds to. -->
    <div v-if="shown >= plan.standings" class="finish-screen__standings">
      <div
        v-for="(row, i) in visibleStandings"
        :key="row.seat"
        class="finish-row score-row--enter"
        :class="{ 'finish-row--winner': i === 0 }"
      >
        <span class="finish-row__place">{{ i + 1 }}</span>
        <span class="finish-row__name">{{ row.name }}</span>
        <span class="finish-row__money"><img src="art/coin-1.jpg" alt="EP" /> {{ row.money }}</span>
      </div>
    </div>

    <p v-if="!revealDone" class="muted finish-screen__skiphint">Tap anywhere to skip ahead</p>

    <template v-if="revealDone">
      <button v-if="!isOnline || isHost" class="primary score-row--enter" @click="again">Race again</button>
      <button class="secondary score-row--enter" @click="leave">Back to the menu</button>
    </template>
  </div>
</template>

<script>
// The end of the race: final standings, and the two betting decks turned
// face up card by card in the order they were placed - the game's whole
// reveal, so it gets its own screen rather than a toast.
//
// The reveal is PACED (scoreTheatre.js - the drama report, 2026-08-29):
// result line first, then each deck card by card, then the standings. The
// schedule is per-client theatre; the engine scored everything long ago.
// A tap deals the rest instantly, and reduced motion skips the wait.
import { mapState, mapGetters } from 'vuex';
import CamelChip from '@/components/game/CamelChip.vue';
import { finalRevealPlan } from '@/utils/scoreTheatre';

export default {
  name: 'ScoreSheet',
  components: { CamelChip },
  data() {
    return { shown: 0, revealTimers: [] };
  },
  computed: {
    ...mapState(['game']),
    ...mapGetters(['isOnline', 'isHost']),
    report() { return this.game?.meta.finalReport || null; },
    plan() { return finalRevealPlan(this.report); },
    revealDone() { return this.shown >= this.plan.total; },
    visibleStandings() {
      if (!this.report) return [];
      return this.report.standings.slice(0, Math.max(0, this.shown - this.plan.standings + 1));
    },
  },
  watch: {
    report() { this.beginReveal(); },
  },
  mounted() { this.beginReveal(); },
  beforeUnmount() { this.clearRevealTimers(); },
  methods: {
    visibleCards(p) {
      const pile = this.report.piles[p];
      return pile.cards.slice(0, Math.max(0, this.shown - this.plan.piles[p].cards + 1));
    },
    beginReveal() {
      this.clearRevealTimers();
      this.shown = 0;
      if (!this.report) return;
      const { at, total } = this.plan;
      // Reduced motion (or an empty plan): everything at once.
      if (!at.length || at.every((ms) => ms === 0)) { this.shown = total; return; }
      at.forEach((ms, i) => {
        this.revealTimers.push(window.setTimeout(() => { this.shown = i + 1; }, ms));
      });
    },
    skipReveal() {
      if (this.revealDone) return;
      this.clearRevealTimers();
      this.shown = this.plan.total;
    },
    clearRevealTimers() {
      this.revealTimers.forEach((t) => window.clearTimeout(t));
      this.revealTimers = [];
    },
    again() {
      if (this.isOnline) {
        if (this.isHost) this.$store.dispatch('dealRoom');
      } else {
        this.$store.dispatch('newGame', {});
      }
    },
    leave() {
      if (this.isOnline) this.$store.dispatch('leaveRoom');
      else this.$store.dispatch('abandonGame');
    },
  },
};
</script>
